import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { functions, db } from './firebase';
import { logger } from '../utils/logger';

export interface SubscriptionStatus {
  active: boolean;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'none';
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd?: boolean;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface PortalSessionResponse {
  url: string;
}

class SubscriptionService {
  private subscriptionListeners: Map<string, Unsubscribe> = new Map();

  /**
   * Create a Stripe Checkout session for the user to subscribe
   */
  async createCheckoutSession(
    successUrl?: string,
    cancelUrl?: string
  ): Promise<CheckoutSessionResponse> {
    try {
      logger.info('Creating Stripe checkout session...');

      const createCheckoutSessionFn = httpsCallable<
        { successUrl?: string; cancelUrl?: string },
        CheckoutSessionResponse
      >(functions, 'createCheckoutSession');

      const result = await createCheckoutSessionFn({
        successUrl,
        cancelUrl,
      });

      logger.info('Checkout session created successfully');
      return result.data;
    } catch (error) {
      logger.error('Failed to create checkout session', error);
      throw new Error(
        `Failed to create checkout session: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Get the current subscription status for the authenticated user
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    try {
      logger.info('Fetching subscription status...');

      const getStatusFn = httpsCallable<void, SubscriptionStatus>(
        functions,
        'getSubscriptionStatus'
      );

      const result = await getStatusFn();

      logger.info('Subscription status retrieved:', result.data);
      return result.data;
    } catch (error) {
      logger.error('Failed to get subscription status', error);
      // Return inactive status on error instead of throwing
      return {
        active: false,
        status: 'none',
      };
    }
  }

  /**
   * Create a billing portal session for managing subscription
   */
  async createPortalSession(returnUrl?: string): Promise<PortalSessionResponse> {
    try {
      logger.info('Creating billing portal session...');

      const createPortalFn = httpsCallable<
        { returnUrl?: string },
        PortalSessionResponse
      >(functions, 'createPortalSession');

      const result = await createPortalFn({
        returnUrl,
      });

      logger.info('Portal session created successfully');
      return result.data;
    } catch (error) {
      logger.error('Failed to create portal session', error);
      throw new Error(
        `Failed to create portal session: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Subscribe to real-time subscription updates
   */
  subscribeToStatus(
    userId: string,
    onUpdate: (status: SubscriptionStatus) => void
  ): Unsubscribe {
    // Clean up existing listener if any
    this.unsubscribeFromStatus(userId);

    logger.info(`Subscribing to subscription status for user ${userId}`);

    const userDocRef = doc(db, 'users', userId);

    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const subscription = data?.subscription;

          if (subscription) {
            const status: SubscriptionStatus = {
              active:
                subscription.status === 'active' ||
                subscription.status === 'trialing',
              status: subscription.status || 'none',
              currentPeriodEnd: subscription.currentPeriodEnd?.toDate(),
              cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
            };

            onUpdate(status);
          } else {
            onUpdate({
              active: false,
              status: 'none',
            });
          }
        } else {
          onUpdate({
            active: false,
            status: 'none',
          });
        }
      },
      (error) => {
        logger.error('Error listening to subscription status', error);
        onUpdate({
          active: false,
          status: 'none',
        });
      }
    );

    this.subscriptionListeners.set(userId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Unsubscribe from subscription status updates
   */
  unsubscribeFromStatus(userId: string): void {
    const unsubscribe = this.subscriptionListeners.get(userId);
    if (unsubscribe) {
      unsubscribe();
      this.subscriptionListeners.delete(userId);
      logger.info(`Unsubscribed from subscription status for user ${userId}`);
    }
  }

  /**
   * Clean up all subscription listeners
   */
  cleanup(): void {
    this.subscriptionListeners.forEach((unsubscribe) => unsubscribe());
    this.subscriptionListeners.clear();
    logger.info('All subscription listeners cleaned up');
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;
